import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, type Variants } from "framer-motion";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  CalendarHeart,
  Sparkles,
  MapPin,
  Landmark,
  PartyPopper,
  Flower2,
} from "lucide-react";
import Petals from "../common/Petals";
import Flourish from "../common/Flourish";
import SplitText from "../common/SplitText";
import { fonts, palette } from "../../theme";

const EASE = [0.16, 1, 0.3, 1] as const;

// ── Styled ────────────────────────────────────────────────────

const Section = styled("section")({
  position: "relative",
  overflow: "hidden",
  padding: "110px 20px 0",
  background:
    "linear-gradient(180deg, #ffffff 0%, #FCF8F2 12%, #F7EFE2 58%, #F2E8D7 100%)",
  "@media (max-width:600px)": { padding: "80px 14px 0" },
});

const Watermark = styled(Box)({
  position: "absolute",
  inset: 0,
  zIndex: 0,
  pointerEvents: "none",
  overflow: "hidden",
  color: palette.gold,
  opacity: 0.05,
});

const Container = styled(Box)({ position: "relative", zIndex: 2 });

const Header = styled(motion.div)({
  textAlign: "center",
  maxWidth: 660,
  margin: "0 auto 56px",
});

const Title = styled(Typography)({
  fontFamily: fonts.display,
  fontSize: "clamp(2rem, 5vw, 3rem)",
  fontWeight: 600,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: palette.ink,
  "& em": { fontStyle: "italic", color: palette.goldDeep },
});

const Subtitle = styled(Typography)({
  fontFamily: fonts.serif,
  fontSize: "1.06rem",
  lineHeight: 1.95,
  color: palette.warmGray,
  marginTop: "18px",
});

// Cartes info
const Cards = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "20px",
  maxWidth: 760,
  margin: "0 auto",
  "@media (max-width:700px)": { gridTemplateColumns: "1fr" },
});

const InfoCard = styled(motion.div)({
  background: "rgba(255,255,255,0.5)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(191,160,106,0.3)",
  borderRadius: 16,
  boxShadow: "0 10px 30px rgba(58,53,47,0.08)",
  padding: "26px 16px",
  textAlign: "center",
  transition: "transform .5s cubic-bezier(.16,1,.3,1), box-shadow .5s ease",
  "&:hover": {
    transform: "translateY(-6px) scale(1.02)",
    boxShadow: "0 22px 46px rgba(58,53,47,0.14)",
  },
});

const IconCircle = styled(Box)({
  width: 46,
  height: 46,
  borderRadius: "50%",
  margin: "0 auto 14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "radial-gradient(circle at 35% 30%, #F0DCB4, #BFA06A)",
  color: "#fff",
  boxShadow: "0 6px 16px rgba(191,160,106,0.4)",
  animation: "floaty 6s ease-in-out infinite",
});

const CardLabel = styled(Typography)({
  fontFamily: fonts.sans,
  fontSize: "0.6rem",
  letterSpacing: "0.24em",
  textTransform: "uppercase",
  color: palette.warmGray,
  marginBottom: "6px",
});

const CardValue = styled(Typography)({
  fontFamily: fonts.display,
  fontSize: "0.98rem",
  color: palette.ink,
  lineHeight: 1.4,
});

// Timeline des événements
const Events = styled(Box)({ position: "relative", maxWidth: 680, margin: "20px auto 0" });

const EventLine = styled(motion.div)({
  position: "absolute",
  left: 23,
  top: 12,
  bottom: 12,
  width: 2,
  transformOrigin: "top",
  background: "linear-gradient(180deg, #D9BE86, #A8884F)",
  "@media (max-width:600px)": { left: 19 },
});

const Milestone = styled(motion.div)({
  position: "relative",
  paddingLeft: "68px",
  marginBottom: "40px",
  "&:last-of-type": { marginBottom: 0 },
  "@media (max-width:600px)": { paddingLeft: "56px" },
});

const MIcon = styled(motion.div)({
  position: "absolute",
  left: 0,
  top: 0,
  width: 48,
  height: 48,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "radial-gradient(circle at 35% 28%, #F0DCB4, #BFA06A 62%, #A8884F)",
  color: "#fff",
  border: "2px solid rgba(255,255,255,0.7)",
  zIndex: 1,
  "@media (max-width:600px)": { width: 40, height: 40, left: -1 },
});

const ECard = styled(Box)({
  background: "rgba(255,255,255,0.45)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  border: "1px solid rgba(191,160,106,0.22)",
  borderRadius: 14,
  padding: "16px 20px",
  transition: "transform .5s cubic-bezier(.16,1,.3,1), box-shadow .5s ease",
  "&:hover": { transform: "translateY(-3px)", boxShadow: "0 16px 36px rgba(58,53,47,0.1)" },
});

const ETitle = styled(Typography)({
  fontFamily: fonts.display,
  fontSize: "1.15rem",
  color: palette.ink,
  marginBottom: "8px",
});

const EText = styled(Typography)({
  fontFamily: fonts.serif,
  fontSize: "1rem",
  lineHeight: 1.85,
  color: palette.warmGray,
});

// Cartes Google Maps
const MapsRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "24px",
  maxWidth: 760,
  margin: "50px auto 0",
  "@media (max-width:600px)": { gridTemplateColumns: "1fr", gap: "28px" },
});

const MapLabel = styled(Typography)({
  fontFamily: fonts.display,
  fontSize: "0.98rem",
  fontWeight: 600,
  color: palette.ink,
});

const MapFrame = styled(Box)({
  position: "relative",
  height: 240,
  marginTop: "10px",
  borderRadius: 16,
  overflow: "hidden",
  border: "1px solid rgba(191,160,106,0.35)",
  boxShadow: "0 14px 36px rgba(58,53,47,0.14)",
  transition: "transform .5s cubic-bezier(.16,1,.3,1), box-shadow .5s ease",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: "0 22px 50px rgba(191,160,106,0.3)",
  },
  "& iframe": { position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" },
  "@media (max-width:600px)": { height: 220 },
});

const Shimmer = styled(Box)({
  position: "absolute",
  inset: 0,
  background: "linear-gradient(90deg, #efe7da 0%, #f8f2e8 50%, #efe7da 100%)",
  backgroundSize: "840px 100%",
  animation: "shimmer 1.4s linear infinite",
});

// Bandeau "Serez-vous présent(e)"
const Attending = styled(Box)({
  position: "relative",
  marginTop: "90px",
  padding: "90px 20px 100px",
  textAlign: "center",
  background: "linear-gradient(180deg, #F2E8D7 0%, #ECDFC9 100%)",
});

const AttendingTitle = styled(Typography)({
  fontFamily: fonts.display,
  fontSize: "1.5rem",
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: palette.ink,
  marginBottom: "16px",
});

const AttendingText = styled(Typography)({
  fontFamily: fonts.serif,
  fontSize: "1.06rem",
  lineHeight: 1.9,
  color: palette.warmGray,
  maxWidth: 480,
  margin: "0 auto",
});

// ── Variants timeline (séquence : icône → titre → paragraphe) ──

const milestoneContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
};
const iconV: Variants = {
  hidden: { scale: 0, rotate: -30, opacity: 0 },
  visible: {
    scale: [0, 1.18, 1],
    rotate: [-30, 0, 0],
    opacity: 1,
    boxShadow: [
      "0 0 0 rgba(191,160,106,0)",
      "0 0 22px rgba(191,160,106,0.75)",
      "0 6px 16px rgba(191,160,106,0.4)",
    ],
    transition: { duration: 0.9, ease: EASE },
  },
};
const fadeV: Variants = {
  hidden: { opacity: 0, y: 16, filter: "blur(6px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.7, ease: EASE } },
};

// ── Carte Google Maps avec placeholder shimmer ────────────────

function MapCard({
  label,
  src,
  title,
}: {
  label: string;
  src: string;
  title: string;
}) {
  const [loaded, setLoaded] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.9, ease: EASE }}
    >
      <MapLabel>
        <SplitText text={label} stagger={0.015} />
      </MapLabel>
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
        style={{
          height: 2,
          width: 56,
          marginTop: 6,
          background: "linear-gradient(90deg, #BFA06A, transparent)",
          transformOrigin: "left",
        }}
      />
      <MapFrame>
        {!loaded && <Shimmer />}
        <iframe
          title={title}
          src={src}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          style={{ opacity: loaded ? 1 : 0, transition: "opacity 0.6s ease" }}
        />
      </MapFrame>
    </motion.div>
  );
}

// ── L'idée signature : le voyage Danemark → Allemagne ─────────

function TravelConnection() {
  const { t } = useTranslation();
  const route = "M40 86 C 140 16, 260 16, 360 70";
  return (
    <Box sx={{ textAlign: "center", my: "44px", position: "relative", zIndex: 2 }}>
      <Box sx={{ maxWidth: 440, mx: "auto" }}>
        <svg viewBox="0 0 400 120" width="100%" style={{ overflow: "visible" }}>
          <motion.path
            d={route}
            fill="none"
            stroke="#BFA06A"
            strokeWidth={2}
            strokeDasharray="2 7"
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 2.2, ease: "easeInOut" }}
          />
          <circle cx="40" cy="86" r="5" fill="#A8884F" />
          <circle cx="360" cy="70" r="5" fill="#A8884F" />
          <text x="40" y="108" textAnchor="middle" fontSize="11" fill={palette.warmGray} fontFamily={fonts.sans}>
            Ribe · DK
          </text>
          <text x="360" y="92" textAnchor="middle" fontSize="11" fill={palette.warmGray} fontFamily={fonts.sans}>
            Gießen · DE
          </text>
          <motion.g
            initial={{ offsetDistance: "0%", opacity: 0 }}
            whileInView={{ offsetDistance: "100%", opacity: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 2.2, ease: "easeInOut" }}
            style={{
              offsetPath: `path("${route}")`,
              offsetRotate: "0deg",
              filter: "drop-shadow(0 0 5px rgba(191,160,106,0.9))",
            }}
          >
            <path
              d="M0 1.5 C -1.4 -1, -5 0, -5 2.8 C -5 5.6, -1.4 7, 0 8.4 C 1.4 7, 5 5.6, 5 2.8 C 5 0, 1.4 -1, 0 1.5 Z"
              fill="#BFA06A"
            />
          </motion.g>
        </svg>
      </Box>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.9, delay: 1.4, ease: EASE }}
        style={{
          fontFamily: fonts.serif,
          fontStyle: "italic",
          fontSize: "1.06rem",
          color: palette.goldDeep,
          maxWidth: 460,
          margin: "8px auto 0",
        }}
      >
        {t("whereWhen.journey")}
      </motion.div>
    </Box>
  );
}

// ── Composant ─────────────────────────────────────────────────

export default function WhereWhen() {
  const { t } = useTranslation();

  const infoCards = [
    { icon: <CalendarHeart size={22} strokeWidth={1.6} />, label: t("whereWhen.cardDate"), value: t("whereWhen.infoDate") },
    { icon: <Sparkles size={22} strokeWidth={1.6} />, label: t("whereWhen.cardEvent"), value: t("whereWhen.infoTime") },
    { icon: <MapPin size={22} strokeWidth={1.6} />, label: t("whereWhen.cardPlace"), value: t("whereWhen.infoPlace") },
  ];

  return (
    <Section>
      <Petals count={8} opacity={0.32} />
      <Watermark aria-hidden>
        <Flower2 size={260} style={{ position: "absolute", top: -40, left: -50 }} strokeWidth={0.6} />
        <Flower2 size={300} style={{ position: "absolute", bottom: 120, right: -70 }} strokeWidth={0.6} />
      </Watermark>

      <Container>
        {/* En-tête */}
        <Header
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1, ease: EASE }}
        >
          <Title>
            <SplitText text={t("whereWhen.titlePre") + " "} stagger={0.05} />
            <em>
              <SplitText text={t("whereWhen.titleEm")} stagger={0.05} delay={0.25} />
            </em>
          </Title>
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            whileInView={{ scaleX: 1, opacity: 1 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 1, delay: 0.5, ease: EASE }}
            style={{
              height: 2,
              width: 130,
              margin: "16px auto 0",
              background: "linear-gradient(90deg, transparent, #BFA06A, transparent)",
              transformOrigin: "center",
            }}
          />
          <Subtitle>{t("whereWhen.subtext")}</Subtitle>
        </Header>

        {/* Cartes info */}
        <Cards>
          {infoCards.map((c, i) => (
            <InfoCard
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.8, delay: i * 0.15, ease: EASE }}
            >
              <IconCircle sx={{ animationDelay: `${i * 0.8}s` }}>{c.icon}</IconCircle>
              <CardLabel>{c.label}</CardLabel>
              <CardValue>{c.value}</CardValue>
            </InfoCard>
          ))}
        </Cards>

        <Flourish />

        {/* Timeline des deux événements */}
        <Events>
          <EventLine
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1.2, ease: EASE }}
          />

          {/* Mariage civil */}
          <Milestone
            variants={milestoneContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
          >
            <MIcon variants={iconV}>
              <Landmark size={20} strokeWidth={1.6} />
            </MIcon>
            <ECard>
              <motion.div variants={fadeV}>
                <ETitle>{t("whereWhen.ceremonyTitle")}</ETitle>
              </motion.div>
              <motion.div variants={fadeV}>
                <EText>
                  {t("whereWhen.ceremonyIntro")}
                  <br />
                  <strong>“Det Gamle Rådhus” (das Alte Rathaus), Von Støckens Plads, DK-6760 Ribe</strong>
                  <br />
                  {t("whereWhen.ceremonyOutro")}
                </EText>
              </motion.div>
            </ECard>
          </Milestone>

          {/* Célébration & Réception */}
          <Milestone
            variants={milestoneContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
          >
            <MIcon variants={iconV}>
              <PartyPopper size={20} strokeWidth={1.6} />
            </MIcon>
            <ECard>
              <motion.div variants={fadeV}>
                <ETitle>{t("whereWhen.receptionTitle")}</ETitle>
              </motion.div>
              <motion.div variants={fadeV}>
                <EText>
                  {t("whereWhen.receptionIntro")}
                  <br />
                  <strong>Grünberger Straße 143, 35394 Gießen (Restaurant Miller Haus)</strong>
                  <br />
                  {t("whereWhen.receptionOutro")}
                </EText>
              </motion.div>
            </ECard>
          </Milestone>
        </Events>

        {/* Voyage Danemark → Allemagne */}
        <TravelConnection />

        {/* Cartes Google Maps */}
        <MapsRow>
          <MapCard
            label={`${t("whereWhen.ceremonyTitle")} — Ribe`}
            title="Det Gamle Rådhus, Ribe"
            src="https://www.google.com/maps?q=Det%20Gamle%20R%C3%A5dhus%2C%20Von%20St%C3%B8ckens%20Plads%2C%206760%20Ribe%2C%20Danmark&z=16&output=embed"
          />

          <MapCard
            label={`${t("whereWhen.receptionTitle")} — Gießen`}
            title="Grünberger Straße 143, 35394 Gießen (Restaurant Miller Haus)"
            src="https://www.google.com/maps?q=Gr%C3%BCnberger%20Stra%C3%9Fe%20143%2C%2035394%20Gie%C3%9Fen%2C%20Germany&z=15&output=embed"
          />
        </MapsRow>
      </Container>

      {/* Bandeau "Serez-vous présent(e)" */}
      <Attending>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <Flourish />
          <AttendingTitle>
            <SplitText text={t("whereWhen.attendingTitle")} stagger={0.04} />
          </AttendingTitle>
          <AttendingText>{t("whereWhen.attendingText")}</AttendingText>
        </motion.div>
      </Attending>
    </Section>
  );
}
